import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Mic, MicOff, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VoiceCommand({ 
  onSearch, 
  onFilterPriority, 
  onFilterStatus, 
  onFilterSystem,
  onFilterCountry,
  onClearFilters,
  onCreateTask,
  onExportPdf,
  onShowStats,
  className = ""
}) {
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const [transcript, setTranscript] = useState("");
  const navigate = useNavigate();

  // Initialize speech recognition
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      
      if (SpeechRecognition) {
        setIsSupported(true);
        const recognitionInstance = new SpeechRecognition();
        recognitionInstance.continuous = false;
        recognitionInstance.interimResults = true;
        recognitionInstance.lang = 'it-IT'; // Italian
        
        recognitionInstance.onstart = () => {
          setIsListening(true);
          setTranscript("");
        };
        
        recognitionInstance.onresult = (event) => {
          const current = event.resultIndex;
          const result = event.results[current];
          const transcriptText = result[0].transcript;
          setTranscript(transcriptText);
          
          if (result.isFinal) {
            processCommandWithAI(transcriptText.trim());
          }
        };
        
        recognitionInstance.onerror = (event) => {
          console.error('Speech recognition error:', event.error);
          setIsListening(false);
          
          if (event.error === 'no-speech') {
            toast.info("Nessun audio rilevato. Riprova.");
          } else if (event.error === 'not-allowed') {
            toast.error("Permesso microfono negato. Abilita il microfono nelle impostazioni.");
          } else {
            toast.error(`Errore: ${event.error}`);
          }
        };
        
        recognitionInstance.onend = () => {
          setIsListening(false);
        };
        
        setRecognition(recognitionInstance);
      }
    }
  }, []);

  const processCommandWithAI = useCallback(async (command) => {
    if (!command) return;
    
    setIsProcessing(true);
    setIsListening(false);
    
    try {
      // Send command to AI backend
      const response = await axios.post(`${API}/ai/voice-command`, {
        command: command
      });
      
      const { action, params, message, success } = response.data;
      
      if (!success) {
        toast.error(message || "Comando non riconosciuto");
        return;
      }
      
      // Show AI response message
      toast.success(
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-yellow-500" />
          <span>{message}</span>
        </div>,
        { duration: 3000 }
      );
      
      // Execute the action
      switch (action) {
        case "search":
          if (params.query) {
            onSearch?.(params.query);
          }
          break;
          
        case "filter":
          if (params.priority) {
            onFilterPriority?.(params.priority);
          }
          if (params.status) {
            onFilterStatus?.(params.status);
          }
          if (params.system) {
            onFilterSystem?.(params.system);
          }
          if (params.country) {
            onFilterCountry?.(params.country);
          }
          break;
          
        case "navigate":
          if (params.destination) {
            const routes = {
              dashboard: '/dashboard',
              tasks: '/tasks',
              kanban: '/kanban',
            };
            navigate(routes[params.destination] || '/dashboard');
          }
          break;
          
        case "create":
          onCreateTask?.(params.info || "");
          break;
          
        case "export":
          onExportPdf?.();
          break;
          
        case "clear":
          onClearFilters?.();
          break;
          
        case "info":
          // Show stats info via toast
          if (params.query_type === "count") {
            onShowStats?.(params);
          } else {
            toast.info("Per vedere le statistiche, vai alla Dashboard");
          }
          break;
          
        default:
          toast.info("Non ho capito il comando. Prova con: cerca, filtra, vai a, nuovo task");
      }
      
    } catch (error) {
      console.error('AI command error:', error);
      toast.error("Errore nell'elaborazione. Riprova.");
    } finally {
      setIsProcessing(false);
      setTranscript("");
    }
  }, [navigate, onSearch, onFilterPriority, onFilterStatus, onFilterSystem, onFilterCountry, onClearFilters, onCreateTask, onExportPdf, onShowStats]);

  const toggleListening = () => {
    if (!recognition) return;
    
    if (isListening) {
      recognition.stop();
    } else if (!isProcessing) {
      try {
        recognition.start();
        toast.info(
          <div className="flex items-center gap-2">
            <Mic className="w-4 h-4 text-red-500 animate-pulse" />
            <span>In ascolto... Parla ora!</span>
          </div>,
          { duration: 2000 }
        );
      } catch (error) {
        console.error('Error starting recognition:', error);
        toast.error("Errore nell'avvio del microfono");
      }
    }
  };

  if (!isSupported) {
    return null;
  }

  return (
    <div className={`relative ${className}`}>
      <Button
        variant={isListening ? "default" : "outline"}
        size="icon"
        onClick={toggleListening}
        disabled={isProcessing}
        className={`relative ${isListening ? 'bg-red-500 hover:bg-red-600 animate-pulse' : ''}`}
        data-testid="voice-command-btn"
        title={isListening ? "Stop ascolto" : "Comando vocale AI"}
      >
        {isProcessing ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : isListening ? (
          <MicOff className="w-4 h-4" />
        ) : (
          <div className="relative">
            <Mic className="w-4 h-4" />
            <Sparkles className="w-2 h-2 absolute -top-1 -right-1 text-yellow-500" />
          </div>
        )}
      </Button>
      
      {/* Transcript overlay */}
      {(isListening || isProcessing) && (
        <div className="absolute top-full mt-2 right-0 bg-popover border border-border rounded-lg px-4 py-3 shadow-lg z-50 min-w-[250px]">
          {isListening && (
            <>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                <span className="text-xs text-muted-foreground">In ascolto...</span>
              </div>
              {transcript && (
                <p className="text-sm font-medium">{transcript}</p>
              )}
            </>
          )}
          {isProcessing && (
            <div className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-primary" />
              <span className="text-sm">Elaboro con AI...</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
